import { useSignalStore } from '@/page/token/signal/store';
import signalApiService from '@/request/signal/signal.api.service';
import { ISignal } from '@/request/signal/signal.response.interface';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWsSignalToken } from './useWsSignalToken';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import BigNumber from 'bignumber.js';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import { debounce } from 'lodash';

export const useGetSignalList = () => {
  const { bscSignalTradingSetting, solSignalTradingSetting, setBscSignalTradingSetting, setSolSignalTradingSetting, filterConfig } = useSignalStore();
  const [loading, setLoading] = useState(false);
  const [signalList, setSignalList] = useState<ISignal[]>([]);
  const { subscribe } = useAppEventBusContext();
  const [keyword, setKeyword] = useState('');
  const { chain } = useBoomWallet();
  const [filteredList, setFilteredList] = useState<ISignal[]>([]);
  const [searchTokenList, setSearchTokenList] = useState<ISignal[]>([]);
  const targetChainTradingSetting = chain === 'bsc' ? bscSignalTradingSetting : solSignalTradingSetting;
  const { merge } = targetChainTradingSetting;
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  useEffect(() => {
    if (keyword.length > 0) {
      debouncedFilter(keyword);
    }
  }, [keyword]);

  const clearKeyword = () => {
    setKeyword('');
  };

  const fetchSignalList = async () => {
    try {
      if (chain) {
        setLoading(true);
        const res = await signalApiService.getSignalListApi();
        if (res.code == 0) {
          setSignalList(res.data);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchSignalList();
  }, [chain]);

  const debouncedFilter = useCallback(
    debounce((value) => {
      fetchKeywordList(value);
    }, 500),
    [],
  );

  const fetchKeywordList = async (keyword: string) => {
    try {
      setLoading(true);
      const res = await signalApiService.getSignalSearchApi({ keyword });
      if (res.code === 0) {
        setSearchTokenList(res.data);
      }
    } catch (error) {
      console.error('搜索接口调用失败', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredList = () => {
    let filteredList = keyword.length > 0 ? searchTokenList : signalList;

    // filterConfig 筛选
    if (filterConfig) {
      filteredList = filteredList.filter((signal) => {
        // 提醒次数筛选
        if (filterConfig.alertTimes.start && signal.pushTimes < Number(filterConfig.alertTimes.start)) return false;
        if (filterConfig.alertTimes.end && signal.pushTimes > Number(filterConfig.alertTimes.end)) return false;

        // 市值筛选
        const mc = BigNumber(signal.alert.price).times(signal.totalSupply).div(1000).toString();
        if (filterConfig.mc.start && Number(mc) < Number(filterConfig.mc.start)) return false;
        if (filterConfig.mc.end && Number(mc) > Number(filterConfig.mc.end)) return false;

        // 流动性筛选
        if (filterConfig.liquidity.start && Number(signal.alert.liquidity) < Number(filterConfig.liquidity.start)) return false;
        if (filterConfig.liquidity.end && Number(signal.alert.liquidity) > Number(filterConfig.liquidity.end)) return false;

        // 持有人数筛选
        if (filterConfig.holders.start && signal.alert.holders < Number(filterConfig.holders.start)) return false;
        if (filterConfig.holders.end && signal.alert.holders > Number(filterConfig.holders.end)) return false;

        return true;
      });
    }

    // 合并处理
    if (merge) {
      const addressMap = new Map<string, ISignal>();
      filteredList.forEach((signal) => {
        const existing = addressMap.get(signal.address);
        if (!existing || existing.timestamp < signal.timestamp) {
          addressMap.set(signal.address, signal);
        }
      });
      return Array.from(addressMap.values()).sort((a, b) => b.timestamp - a.timestamp);
    }
    return [...filteredList].sort((a, b) => b.timestamp - a.timestamp);
  };
  useEffect(() => {
    const applyFilters = async () => {
      try {
        const filtered = await getFilteredList();
        setFilteredList(filtered);
      } catch (error) {
        console.error('应用过滤器失败', error);
      }
    };

    applyFilters();
  }, [keyword, signalList, filterConfig, searchTokenList, merge]);

  const tokenAddressList = useMemo(() => filteredList.map((item) => item.address), [filteredList]);

  useWsSignalToken(tokenAddressList);

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Update_Signal, (data: ISignal) => {
      setSignalList((prev) => [data, ...prev]);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Focus_Token, (address: string) => {
      setKeyword(address);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (keyword.length == 0) {
      if (chain === 'bsc') {
        setBscSignalTradingSetting({ ...bscSignalTradingSetting, merge: true });
      } else {
        setSolSignalTradingSetting({ ...solSignalTradingSetting, merge: true });
      }
    } else {
      if (chain === 'bsc') {
        setBscSignalTradingSetting({ ...bscSignalTradingSetting, merge: false });
      } else {
        setSolSignalTradingSetting({ ...solSignalTradingSetting, merge: false });
      }
    }
  }, [keyword]);

  return {
    loading,
    renderList: filteredList,
    keyword,
    onInputChange,
    setKeyword,
    clearKeyword,
  };
};
